import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import MDEditor from '@uiw/react-md-editor';
import { JSONSchema7 } from 'json-schema';

import { ContentCopy, Link } from '@mui/icons-material';
import { Avatar, Box, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Tooltip, Typography } from '@mui/material';

import { JSONEditor } from '../../../components/json-editor';
import { sqlpadService } from '../../../services/sqlpad-service';
import { BLOCK_FILE_PATH } from '../../../utils/constants/block';
import { FileState } from '../../../utils/models/common-models';
import { Connection, Schema, StatementColumn } from '../../../utils/models/sqlpad';
import SqlEditor from './SqlEditor';


interface DbLookupProps {
    selectedFile: string;
    fileStates: FileState;
    setFileStates: Dispatch<SetStateAction<FileState>>;
}

const CONFIG_JSON_SCHEMA: JSONSchema7 = {
    "type": "array",
    "$schema": "http://json-schema.org/draft-07/schema",
    "items": {
        "type": "object",
        "properties": {
            "namespace": {
                "type": "string"
            },
            "connectionId": {
                "type": "string",
                "format": "uuid"
            }
        },
        "required": [
            "namespace",
            "connectionId"
        ],
        "additionalProperties": false
    }
}

const wait = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

const DbLookup: React.FC<DbLookupProps> = ({
    selectedFile,
    fileStates,
    setFileStates,
}) => {

    const [connectionsList, setConnectionsList] = useState<Connection[]>([]);
    const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
    const [selectedConnectionSchema, setSelectedConnectionSchema] = useState<Schema[]>([]);
    const [queryResultColumns, setQueryResultColumns] = useState<StatementColumn[]>([]);
    const [queryResultData, setQueryResultData] = useState<any[][]>([]);
    const [errorInQueryResult, setErrorInQueryResult] = useState<string>('');
    const [errorInConnectionSchema, setErrorInConnectionSchema] = useState<string>('');
    const [isQueryResultsLoading, setIsQueryResultsLoading] = useState<boolean>(false);
    const [hasQueryRun, setHasQueryRun] = useState<boolean>(false);

    const getDbConnectionsList = async () => {
        try {
            const response = await sqlpadService.getConnectionsList();
            console.log('==>get connections list response: ', response)
            setConnectionsList(response);
        } catch (error: any) {
            console.log('error in getting connections list: ', error)
            alert('some error occurred while getting connection list')
        }
    }

    const getDbConnectionSchema = useCallback(async (id: string) => {
        try {
            const response = await sqlpadService.getConnectionSchema(id)
            console.log('==>get connection schema response: ', response);
            setSelectedConnectionSchema(response?.schemas || [])
        } catch (error: any) {
            console.log('error in getting connection schema: ', error)
            if (error?.status === 400) {
                setErrorInConnectionSchema(error?.response?.data?.title)
                return;
            }
            alert('some error occurred while getting connection schema')
        }
    }, [])

    const fetchDbResultOnRunQuery = async () => {
        if (!selectedConnectionId) return;

        setErrorInQueryResult('');
        setIsQueryResultsLoading(true);
        setHasQueryRun(true);
        setQueryResultColumns([]);
        setQueryResultData([]);

        sqlpadService.createBatches({
            connectionId: selectedConnectionId,
            batchText: fileStates[selectedFile],
            connectionClientId: '',
            selectedText: '',
            name: '',
        })
            .then(async (createBatchRes) => {
                console.log('==> create batch response: ', createBatchRes);
                const batchId = createBatchRes.statements.length > 0 ? createBatchRes.statements[0].batchId : null;
                if (!batchId) return;

                let statusDef = createBatchRes;
                while (statusDef.status !== 'finished' && statusDef.status !== 'error') {
                    try {
                        await wait(300);
                        const batchDetailsRes = await sqlpadService.getBatchDetails(batchId)
                        console.log('==> batch details for columns: ', batchDetailsRes);
                        statusDef = batchDetailsRes;
                    } catch (getBatchError: any) {
                        console.log('error while fetching batch details for columns: ', getBatchError)
                    }
                }

                if (statusDef.statements?.length > 0 && statusDef.statements[0].columns) {
                    setQueryResultColumns(statusDef.statements[0]?.columns)
                }
                return statusDef;
            })
            .then(async (batchRes) => {
                if (!(batchRes && batchRes.statements?.length > 0)) return;
                if (batchRes.status === 'error' && batchRes.statements[0].error) {
                    setErrorInQueryResult(batchRes.statements[0].error?.title);
                    setQueryResultData([]);
                    return;
                }

                console.log('--------columns fetched---------', batchRes)

                sqlpadService.getStatementForResults(batchRes.statements[0].id)
                    .then(queryResult => {
                        console.log('==> query response result: ', queryResult)
                        setQueryResultData(queryResult);
                    })
                    .catch((queryResError: any) => {
                        console.log('error while fetching query results data: ', queryResError)
                    })
            })
            .catch((createBatchError: any) => {
                console.log('error while creating a batch: ', createBatchError)
            })
            .finally(() => {
                setIsQueryResultsLoading(false);
            })
    }

    useEffect(() => {
        if (selectedConnectionId) {
            getDbConnectionSchema(selectedConnectionId)
        }
    }, [getDbConnectionSchema, selectedConnectionId])


    useEffect(() => {
        getDbConnectionsList();
    }, [])


    return (
        <>
            {selectedFile.endsWith(".md") && (
                <div data-color-mode="light" style={{ height: '100%', width: '100%' }}>
                    <MDEditor
                        value={fileStates[selectedFile]}
                        onChange={(value) => setFileStates(prevData => ({ ...prevData, [selectedFile]: value }))}
                        height="100%"
                    />
                </div>
            )}

            {selectedFile === BLOCK_FILE_PATH.config && (
                <Box display='flex' height='100%'>
                    <Box width='70%'>
                        <JSONEditor
                            hideSchemaBox
                            // path="input_json.json"
                            language="json"
                            schemaValue={JSON.stringify(CONFIG_JSON_SCHEMA)}
                            isSchemaSampleDataOn={false}
                            value={fileStates[selectedFile]}
                            onChange={(value: string | undefined) => {
                                setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "{}" }))
                            }}
                        />
                    </Box>

                    <Box width='30%' display={'flex'} flexDirection={'column'}>
                        <Box display='flex' alignItems='center' minHeight='41px' justifyContent='center' sx={{ borderBottom: 1, borderBottomColor: 'divider' }}>
                            <Typography variant='body1' component='span' >Connections List</Typography>
                        </Box>

                        <Box overflow='auto'>
                            <List>
                                {connectionsList.map((connection, index) => (
                                    <Box key={connection.id}>
                                        <ListItem secondaryAction={
                                            <Tooltip title='Copy to Clipboard'>
                                                <IconButton edge="end" aria-label="copy" onClick={async () => {
                                                    await navigator.clipboard.writeText(connection.id);
                                                }}>
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                        }>
                                            <ListItemAvatar>
                                                <Avatar>
                                                    <Link color='primary' />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={connection.name}
                                                secondary={'ID: ' + connection.id}
                                            />
                                        </ListItem>

                                        {index !== (connectionsList.length - 1) && < Divider variant='middle' />}
                                    </Box>
                                ))}
                            </List>
                        </Box>
                    </Box>
                </Box>
            )}

            {selectedFile?.endsWith(".json") && selectedFile !== BLOCK_FILE_PATH.config && (
                <JSONEditor
                    // path="input_json.json"
                    // schemaValue={"{}"}
                    language="json"
                    isSchemaSampleDataOn={false}
                    value={fileStates[selectedFile]}
                    onChange={(value: string | undefined) => {
                        setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "{}" }))
                    }}
                />
            )}

            {selectedFile.endsWith(".txt") && (
                <JSONEditor
                    language="txt"
                    isSchemaSampleDataOn={false}
                    value={fileStates[selectedFile]}
                    onChange={(value: string | undefined) => {
                        setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "" }))
                    }}
                />
            )}

            {selectedFile === "Dockerfile" && (
                <JSONEditor
                    language="dockerfile"
                    // editorReadOnly={true}
                    isSchemaSampleDataOn={false}
                    value={fileStates[selectedFile]}
                    onChange={(value: string | undefined) => {
                        setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "" }))
                    }}
                />
            )}

            {selectedFile.endsWith(".sql") && (
                <SqlEditor
                    value={fileStates[selectedFile]}
                    connectionSchema={selectedConnectionSchema}
                    connectionsList={connectionsList}
                    selectedConnectionId={selectedConnectionId}
                    queryResultColumns={queryResultColumns}
                    queryResultData={queryResultData}
                    errorInQueryResult={errorInQueryResult}
                    errorInConnectionSchema={errorInConnectionSchema}
                    isQueryResultsLoading={isQueryResultsLoading}
                    hasQueryRun={hasQueryRun}
                    setSelectedConnectionId={setSelectedConnectionId}
                    setErrorInConnectionSchema={setErrorInConnectionSchema}
                    onChange={(value: string) => {
                        setFileStates(prevData => ({ ...prevData, [selectedFile]: value }))
                    }}
                    fetchDbResultOnRunQuery={fetchDbResultOnRunQuery}
                />
            )}
        </>
    )
}

export default DbLookup